/**
 * Mini Cart - Funcionalidades do carrinho lateral
 */

class MiniCartManager {
  constructor() {
    this.miniCart = document.querySelector('mini-cart');
    this.cartIcon = document.getElementById('cart-icon-bubble');
    this.isUpdating = false;
    this.refreshPromise = null;
    this.lastObservedCartCount = null;
    
    this.init();
  }

  init() {
    if (!this.miniCart) return;

    // Abrir mini-cart ao clicar no ícone
    if (this.cartIcon) {
      this.cartIcon.addEventListener('click', async (e) => {
        e.preventDefault();

        await this.refresh();
        this.open();
      });
    }

    // Observar mudanças externas no contador do carrinho (ex: apps de terceiros)
    this.setupCartBubbleObserver();

    // Event listeners para quantidade
    this.setupQuantityControls();

    // Event listeners para remover item
    this.setupRemoveButtons();

    // Event listeners para lenço
    this.setupLencolChoice();

    // Atualizar ao adicionar produto ao carrinho
    document.addEventListener('cart:item-added', async (event) => {
      if (this.renderBundledSections(event.detail?.cartData)) {
        this.open();
        return;
      }

      const refreshed = await this.refresh();

      if (refreshed) {
        this.open();
        return;
      }

      window.location.href = window.routes?.cart_url || '/cart';
    });
  }

  setupCartBubbleObserver() {
    try {
      const bubbleSpan = document.querySelector('.cart-count-bubble span[aria-hidden="true"]');
      if (!bubbleSpan) return;

      // Estado inicial observado
      this.lastObservedCartCount = (bubbleSpan.textContent || '').trim();

      const observer = new MutationObserver(() => {
        const newCount = (bubbleSpan.textContent || '').trim();
        if (newCount === this.lastObservedCartCount) return;
        this.lastObservedCartCount = newCount;

        // Se o dataset já está sincronizado com esse valor, a mudança veio do próprio MiniCartManager
        if (bubbleSpan.dataset.miniCartCount === newCount) return;

        // Mudança externa detectada (ex: app adicionou item e só atualizou o bubble)
        this.refresh();
      });

      observer.observe(bubbleSpan, {
        characterData: true,
        childList: true,
        subtree: true,
      });
    } catch (error) {
      console.error('Erro ao observar o contador do carrinho:', error);
    }
  }

  open() {
    if (this.miniCart) {
      this.miniCart.open();
    }
  }

  close() {
    if (this.miniCart) {
      this.miniCart.close();
    }
  }

  setupQuantityControls() {
    // Botões de aumentar/diminuir
    const decreaseButtons = this.miniCart.querySelectorAll('[data-qty-decrease]');
    const increaseButtons = this.miniCart.querySelectorAll('[data-qty-increase]');
    const qtyInputs = this.miniCart.querySelectorAll('.mini-cart-qty-input');

    // Função auxiliar para atualizar o estado (habilitar/desabilitar) do botão de aumento
    const updateIncreaseButtonState = (line) => {
      const input = this.miniCart.querySelector(`.mini-cart-qty-input[data-line="${line}"]`);
      const btn = this.miniCart.querySelector(`[data-qty-increase][data-line="${line}"]`);
      if (!input || !btn) return;

      const currentQty = parseInt(input.value) || 0;
      const maxQty = parseInt(input.dataset.maxQty || '999999');

      if (currentQty >= maxQty) {
        btn.disabled = true;
        btn.classList.add('is-disabled');
      } else {
        btn.disabled = false;
        btn.classList.remove('is-disabled');
      }
    };

    decreaseButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const line = btn.dataset.line;
        const input = this.miniCart.querySelector(`.mini-cart-qty-input[data-line="${line}"]`);
        const currentQty = parseInt(input.value);
        
        if (currentQty > 1) {
          this.updateQuantity(line, currentQty - 1);
        } else {
          this.removeItem(line);
        }

        // Ao diminuir, podemos reabilitar o botão de aumento, se necessário
        updateIncreaseButtonState(line);
      });
    });

    increaseButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const line = btn.dataset.line;
        const input = this.miniCart.querySelector(`.mini-cart-qty-input[data-line="${line}"]`);
        const currentQty = parseInt(input.value) || 0;
        const maxQty = parseInt(input.dataset.maxQty || '999999');

        // Se já está no limite, apenas garante que o botão fique desabilitado
        if (currentQty >= maxQty) {
          btn.disabled = true;
          btn.classList.add('is-disabled');
          return;
        }

        this.updateQuantity(line, currentQty + 1);

        // Atualiza estado para caso tenha acabado de atingir o limite
        updateIncreaseButtonState(line);
      });
    });

    // Input direto
    qtyInputs.forEach(input => {
      const line = input.dataset.line;

      input.addEventListener('change', (e) => {
        let newQty = parseInt(input.value) || 0;
        const maxQty = parseInt(input.dataset.maxQty || '999999');

        if (newQty > maxQty) {
          newQty = maxQty;
          input.value = maxQty;
        }

        if (newQty > 0) {
          this.updateQuantity(line, newQty);
        } else {
          this.removeItem(line);
        }

        updateIncreaseButtonState(line);
      });

      // Estado inicial ao carregar o mini-cart
      updateIncreaseButtonState(line);
    });
  }

  setupRemoveButtons() {
    const removeButtons = this.miniCart.querySelectorAll('[data-cart-remove]');
    
    removeButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const line = btn.dataset.line;
        this.removeItem(line);
      });
    });
  }

  setupLencolChoice() {
    const lencolButtons = this.miniCart.querySelectorAll('[data-lencol-product]');
    
    lencolButtons.forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const productHandle = btn.dataset.lencolProduct;
        
        // Aqui você pode implementar a lógica de adicionar o lenço
        // Por exemplo, buscar o produto e adicionar ao carrinho
        try {
          const response = await fetch(`/products/${productHandle}.js`);
          const product = await response.json();
          
          if (product.variants && product.variants[0]) {
            await this.addToCart(product.variants[0].id, 1);
          }
        } catch (error) {
          console.error('Erro ao adicionar lenço:', error);
        }
      });
    });

    // Setup gift variant option selects
    this.setupGiftVariantSelects();
  }

  setupGiftVariantSelects() {
    const giftOptionsContainers = this.miniCart.querySelectorAll('.mini-cart-gift-options');
    
    console.log('Setup gift variant selects - Found containers:', giftOptionsContainers.length);
    
    giftOptionsContainers.forEach((container, index) => {
      const productId = container.dataset.productId;
      const blockId = container.dataset.giftBlock;
      
      console.log(`Processing product ${index + 1}:`, productId);
      
      // Buscar o script de variantes e o input hidden no contexto do card específico
      const giftCard = container.closest('.mini-cart-gift-card');
      if (!giftCard) {
        console.warn('Gift card not found for product:', productId);
        return;
      }
      
      const variantsScript = giftCard.querySelector(`script[data-product-variants="${productId}"]`);
      if (!variantsScript) {
        console.warn('Variants script not found for product:', productId);
        return;
      }
      
      const variants = JSON.parse(variantsScript.textContent);
      const optionSelects = container.querySelectorAll('.mini-cart-gift-option-select');
      const hiddenInput = giftCard.querySelector(`input[data-gift-variant-id][data-product-id="${productId}"]`);
      
      if (!hiddenInput) {
        console.warn('Hidden input not found for product:', productId);
        return;
      }
      
      console.log(`Product ${productId} setup complete - ${optionSelects.length} option selects`);
      
      // Função para encontrar a variante baseada nas opções selecionadas
      const findVariantByOptions = () => {
        const selectedOptions = {};
        optionSelects.forEach(select => {
          const position = parseInt(select.dataset.optionPosition);
          selectedOptions[position] = select.value;
        });
        
        const variant = variants.find(v => {
          return v.option1 === selectedOptions[1] &&
                 (v.option2 === selectedOptions[2] || !selectedOptions[2]) &&
                 (v.option3 === selectedOptions[3] || !selectedOptions[3]);
        });
        
        return variant;
      };
      
      // Atualizar variant ID quando qualquer opção mudar
      optionSelects.forEach(select => {
        select.addEventListener('change', () => {
          const variant = findVariantByOptions();
          console.log('Variant selected for product', productId, ':', variant);
          if (variant && hiddenInput) {
            hiddenInput.value = variant.id;
            console.log('Updated hidden input to variant ID:', variant.id);
            
            // Atualizar disponibilidade visual se necessário
            if (!variant.available) {
              select.classList.add('unavailable');
            } else {
              select.classList.remove('unavailable');
            }
          } else if (!variant) {
            console.warn('No variant found for selected options');
          }
        });
      });
    });
  }

  async updateQuantity(line, quantity) {
    if (this.isUpdating) return;
    this.isUpdating = true;

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: line,
          quantity: quantity
        })
      });

      if (response.ok) {
        await this.refresh();
      } else {
        console.error('Erro ao atualizar quantidade');
      }
    } catch (error) {
      console.error('Erro na requisição:', error);
    } finally {
      this.isUpdating = false;
    }
  }

  async removeItem(line) {
    if (this.isUpdating) return;
    await this.updateQuantity(line, 0);
  }

  async addToCart(variantId, quantity = 1) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: quantity
          }]
        })
      });

      if (response.ok) {
        await this.refresh();
        return true;
      } else {
        console.error('Erro ao adicionar ao carrinho');
        return false;
      }
    } catch (error) {
      console.error('Erro na requisição:', error);
      return false;
    }
  }

  async refresh() {
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = this.performRefresh();

    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  async performRefresh() {
    try {
      const sectionId = this.miniCart?.dataset.sectionId;
      if (!sectionId) throw new Error('Mini-cart section ID não encontrado');

      const root = window.Shopify?.routes?.root || '/';
      const sectionUrl = `${root}?section_id=${encodeURIComponent(sectionId)}&_=${Date.now()}`;
      const response = await fetch(sectionUrl, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`Falha ao atualizar mini-cart: HTTP ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newMiniCart = doc.querySelector('mini-cart');

      if (!newMiniCart || !this.miniCart) {
        throw new Error('Mini-cart não encontrado na resposta da seção');
      }

      this.replaceMiniCartContents(newMiniCart);

      await this.updateCartCount();
      document.dispatchEvent(new CustomEvent('mini-cart:updated'));

      return true;
    } catch (error) {
      console.error('Erro ao atualizar mini-cart:', error);
      return false;
    }
  }

  renderBundledSections(cartData) {
    const sectionId = this.miniCart?.dataset.sectionId;
    const sections = cartData?.sections;
    const miniCartHtml = sectionId ? sections?.[sectionId] : null;

    if (!miniCartHtml) return false;

    const parser = new DOMParser();
    const miniCartDocument = parser.parseFromString(miniCartHtml, 'text/html');
    const newMiniCart = miniCartDocument.querySelector('mini-cart');

    if (!newMiniCart) return false;

    this.replaceMiniCartContents(newMiniCart);

    const cartIconHtml = sections?.['cart-icon-bubble'];
    if (cartIconHtml && this.cartIcon) {
      const cartIconDocument = parser.parseFromString(cartIconHtml, 'text/html');
      const newCartIcon = cartIconDocument.querySelector('#cart-icon-bubble');

      if (newCartIcon) this.cartIcon.innerHTML = newCartIcon.innerHTML;
    }

    const cartCount = this.cartIcon?.querySelector('.cart-count-bubble span[aria-hidden="true"]');
    if (cartCount) this.lastObservedCartCount = (cartCount.textContent || '').trim();

    document.dispatchEvent(new CustomEvent('mini-cart:updated'));
    return true;
  }

  replaceMiniCartContents(newMiniCart) {
    const wasOpen = this.miniCart.classList.contains('is-open');
    this.miniCart.innerHTML = newMiniCart.innerHTML;

    if (wasOpen) this.miniCart.classList.add('is-open');

    this.setupQuantityControls();
    this.setupRemoveButtons();
    this.setupLencolChoice();
    this.setupGiftVariantSelects();
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      const cartBubble = document.querySelector('.cart-count-bubble span[aria-hidden="true"]');
      if (cartBubble) {
        const newCount = String(cart.item_count);
        cartBubble.textContent = newCount;
        // Marca o valor sincronizado pelo MiniCartManager, para o observer diferenciar
        cartBubble.dataset.miniCartCount = newCount;
        this.lastObservedCartCount = newCount;
      }

      // Mostrar/esconder bubble
      const bubble = document.querySelector('.cart-count-bubble');
      if (bubble) {
        if (cart.item_count > 0) {
          bubble.style.display = 'flex';
        } else {
          bubble.style.display = 'none';
        }
      }
    } catch (error) {
      console.error('Erro ao atualizar contador:', error);
    }
  }
}

// Inicializar quando o DOM estiver pronto
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new MiniCartManager();
  });
} else {
  new MiniCartManager();
}

// // Interceptar formulários de adicionar ao carrinho
// document.addEventListener('submit', async (e) => {
//   if (e.target.matches('form[action="/cart/add"]')) {
//     e.preventDefault();
    
//     const formData = new FormData(e.target);
//     const items = [];
    
//     // Coletar dados do formulário
//     const variantId = formData.get('id');
//     const quantity = formData.get('quantity') || 1;
    
//     if (variantId) {
//       items.push({
//         id: variantId,
//         quantity: parseInt(quantity)
//       });
//     }

//     try {
//       const response = await fetch('/cart/add.js', {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify({ items })
//       });

//       const data = await response.json().catch(() => null);

//       if (response.ok) {
//         // Sucesso: itens adicionados (Shopify pode ter ajustado a quantidade automaticamente)
//         document.dispatchEvent(new CustomEvent('cart:item-added'));
//       } else {
//         // Erros como estoque insuficiente chegam aqui com status 422
//         if (window.discoAlpine && typeof window.discoAlpine.store === 'function') {
//           const cartStore = window.discoAlpine.store('cart');
//           if (cartStore && typeof cartStore.checkAvailability === 'function') {
//             cartStore.checkAvailability(data || {});
//           }
//           // Garante que o estado do carrinho seja sincronizado, caso algo tenha sido parcialmente adicionado
//           if (cartStore && typeof cartStore.fetchItems === 'function') {
//             cartStore.fetchItems();
//           }
//         }

//         // Mesmo em erro, atualizamos o mini-cart para refletir qualquer alteração aplicada pelo backend
//         document.dispatchEvent(new CustomEvent('cart:item-added'));
//       }
//     } catch (error) {
//       console.error('Erro ao adicionar ao carrinho:', error);
//       if (window.discoAlpine && typeof window.discoAlpine.$dispatch === 'function') {
//         window.discoAlpine.$dispatch('show-notification', {
//           type: 'error',
//           title: 'Erro de carrinho',
//           description: 'Erro ao adicionar produto ao carrinho',
//         });
//       }
//     }
//   }
// });
